import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  Output,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import chroma from "chroma-js";
import { jsPlumb } from "jsplumb";
import { take } from "rxjs/operators";
import { LearningObjectiveViewComponent } from "../learning-objective-view/learning-objective-view.component";
import { flatten, getSubjectColor, initColors } from "../shared/helpers/util";
import {
  Domain,
  Goal,
  LearningObjective,
  LearningObjectiveInGoal,
  LearningObjectiveVM,
  Subject,
} from "../shared/model/model";
@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LearningObjectiveViewComponent],
  selector: "app-learning-objective-categories",
  templateUrl: "./learning-objective-categories.component.html",
  styleUrls: ["./learning-objective-categories.component.scss"],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class LearningObjectiveCategoriesComponent implements AfterViewInit {
  selectedGoal: Goal;
  selectedSubjects: Array<Subject> = [];
  domain: Domain;
  scrollPositionSelected = 0;
  // Helper array for looking up goals
  goals: Array<Goal>;
  jsPlumb: any; // jsPlumbInstance;

  public selectedLearningObjective: LearningObjectiveVM;
  private inList: string[];

  @ViewChild("subjectContainer", { static: true }) subjectCo: ElementRef;

  // zIndex = 10000;
  @Input() readOnly = true;
  @Input() learningObjectivesPlanned: Array<LearningObjective>;
  @Input() learningObjectivesOrdered: Array<LearningObjective>;
  @Input() editMode: boolean;
  settingDomain: boolean;

  @Input("selected")
  set selected(lo: LearningObjectiveVM) {
    this.selectedLearningObjective = lo;
    if (lo) {
      // select goal if learning objective is selected and already opened
      const openGoal = (<Array<Goal>>(
        flatten(this.selectedSubjects.map((s) => s.goals))
      )).find((g) => !!g.learningObjectives.find((l) => l.id === lo.id));
      if (openGoal) {
        this.selectGoal(openGoal);
      }
    }
  }

  @Input("learningObjectivesInList")
  set learningObjectivesInList(inlist: string[]) {
    if (this.inList !== inlist && this.domain && inlist) {
      this.learningObjectivesToViewModel(this.domain, inlist);
    }
    this.inList = inlist;
  }

  @Input("domain")
  set domainProp(ds: Domain) {
    const isChanged = !this.domain || (ds && this.domain.id !== ds.id);
    this.setDomain(ds, this.inList);
    if (
      isChanged &&
      this.selectedLearningObjective &&
      ds &&
      this.selectedLearningObjective.domainId === ds.id
    ) {
      // if switched domain and the selected learning objective is in the newly selected domain then expand.
      this.expandSelectedLearningObjective();
    }
  }

  @Output() addLearningObjective: EventEmitter<LearningObjective> =
    new EventEmitter();
  @Output() removeLearningObjective: EventEmitter<LearningObjective> =
    new EventEmitter();
  @Output() learningObjectiveSelected: EventEmitter<LearningObjective> =
    new EventEmitter();

  constructor(private elRef: ElementRef, private zone: NgZone) {}

  public expandSelectedLearningObjective() {
    if (
      this.selectedLearningObjective &&
      this.selectedLearningObjective.id &&
      this.domain &&
      this.domain.id === this.selectedLearningObjective.domainId
    ) {
      this.domain.subjects.forEach((s) => {
        s.goals.forEach((g) => {
          g.learningObjectives.forEach((l) => {
            if (l.id === this.selectedLearningObjective.id) {
              if (this.selectedSubjects.indexOf(s) === -1) {
                this.selectSubject(s);
                this.selectGoal(g);
              }
            }
            l.learningObjectives.forEach((subl) => {
              if (subl.id === this.selectedLearningObjective.id) {
                if (this.selectedSubjects.indexOf(s) === -1) {
                  this.selectSubject(s);
                  this.selectGoal(g);
                }
              }
            });
          });
        });
      });
    }
  }

  public colorScale = (domainId: string, subjectId: string) =>
    getSubjectColor(domainId, subjectId);
  public colorLightScale = (domainId: string, subjectId: string) =>
    chroma(getSubjectColor(domainId, subjectId)).luminance(0.8);

  private setDomain(
    selectedDomain: Domain,
    learningObjectivesInList: string[]
  ) {
    if (!this.settingDomain) {
      this.settingDomain = true;
      if (selectedDomain) {
        if (learningObjectivesInList) {
          this.learningObjectivesToViewModel(
            selectedDomain,
            learningObjectivesInList
          );
        }
        if (this.jsPlumb) {
          this.jsPlumb.deleteEveryEndpoint();
        }
        this.selectedSubjects = [];
        this.domain = selectedDomain;
        this.goals = this.domain.subjects.reduce(
          (result, item) => result.concat(item.goals),
          []
        );
        initColors(selectedDomain);
      }
      this.settingDomain = false;
    }
  }

  private learningObjectivesToViewModel = (
    domain: Domain,
    selected: string[]
  ) => {
    if (domain && domain.subjects) {
      domain.subjects = domain.subjects.map((s) => {
        if (s.goals) {
          s.goals = s.goals.map((g) => {
            g.learningObjectives = g.learningObjectives
              .filter((l) => !l.parentLearningObjectiveId)
              .map((l: LearningObjectiveInGoal) => {
                const learningObjectiveinGoal =
                  selected && selected.find((sel) => sel === l.id)
                    ? !l.inList
                      ? { ...l, inList: true }
                      : l
                    : l.inList
                    ? { ...l, inList: false }
                    : l;
                learningObjectiveinGoal.learningObjectives =
                  !learningObjectiveinGoal.learningObjectives
                    ? g.learningObjectives.filter(
                        (lo) => lo.parentLearningObjectiveId === l.id
                      )
                    : learningObjectiveinGoal.learningObjectives;
                learningObjectiveinGoal.learningObjectives =
                  learningObjectiveinGoal.learningObjectives.map((subl) =>
                    selected && selected.find((sel) => sel === subl.id)
                      ? !subl.inList
                        ? { ...subl, inList: true }
                        : subl
                      : subl.inList
                      ? { ...subl, inList: false }
                      : subl
                  );
                return learningObjectiveinGoal;
              });
            return g;
          });
        }
        return s;
      });
    }
    return domain;
  };

  private drawConnections() {
    this.jsPlumb.deleteEveryEndpoint();
    this.selectedSubjects.forEach((subject) => {
      subject.goals.forEach((goal) => {
        goal.learningObjectives.forEach((lo) => {
          this.jsPlumb.connect({
            source: goal.code,
            target: "categories-" + lo.id,
            connector: ["Straight"],
            anchors: [[1, 0.5, 0, 0, 0, 0], "Left"],
            paintStyle: {
              stroke: getSubjectColor(this.domain.id, subject.code),
              strokeWidth: 2,
            },
          });
        });
      });
    });
    if (this.selectedGoal && this.selectedGoal.in) {
      for (const ingoal of this.selectedGoal.in) {
        let ingoalconnect = ingoal;
        let dashstyle = "0";
        let anchorsStyle = ["Bottom", "Top"];
        let anchorStyle = ["bottom", "top"];

        if (document.getElementById(ingoal) == null) {
          const subjectobject = this.domain.subjects.find(
            (s) => s.goals.find((g) => g.code === ingoal) !== undefined
          );
          ingoalconnect = "subjectcode" + subjectobject.code;
          dashstyle = "2 2";
          anchorsStyle = ["Right", "Top"];
          anchorStyle = ["right", "top"];
        }

        const configOptions: any = {
          source: ingoalconnect, // + 'out',
          target: this.selectedGoal.code, // + 'in',
          connector: [
            "Flowchart",
            {
              stub: [10, 10],
              gap: 0,
              cornerRadius: 30,
              alwaysRespectStubs: false,
            },
          ],
          // anchors: anchorsStyle,
          anchor: ["Continuous", { faces: anchorStyle }],
          paintStyle: {
            stroke: "rgba(49, 49, 82, 0.5)",
            strokeWidth: 2,
            dashstyle: dashstyle,
          },
          endpointStyle: { fillStyle: "#00ABD4", radius: 3 },
          overlays: [["PlainArrow", { width: 8, length: 8, location: 1 }]],
        };

        this.jsPlumb.connect(configOptions);
      }
      if (this.selectedGoal.out) {
        for (const outgoal of this.selectedGoal.out) {
          let outgoalconnect = outgoal;
          let dashstyle = "0";
          let anchorStyle = ["bottom", "top"];

          if (document.getElementById(outgoal) == null) {
            const subjectobject = this.domain.subjects.find(
              (s) => !!s.goals.find((g) => g.code === outgoal)
            );
            if (subjectobject) {
              outgoalconnect = "subjectcode" + subjectobject.code;
              dashstyle = "2 2";
              anchorStyle = ["right", "bottom"];
            }
          }

          const configOptions: any = {
            connector: [
              "Flowchart",
              {
                stub: [10, 10],
                gap: 0,
                cornerRadius: 30,
                alwaysRespectStubs: false,
              },
            ],
            source: this.selectedGoal.code, // + 'out',
            target: outgoalconnect, // + 'in',
            // anchors: anchorsStyle,
            anchor: ["Continuous", { faces: anchorStyle }],
            paintStyle: {
              stroke: "rgba(49, 49, 82, 0.5)",
              strokeWidth: 2,
              dashstyle: dashstyle,
            },
            endpointStyle: { fillStyle: "#313152", radius: 3 },
            overlays: [["PlainArrow", { width: 8, length: 8, location: 1 }]],
          };

          this.jsPlumb.connect(configOptions);
        }
      }
    }
  }

  // @HostListener('window:scroll', ['$event'])
  // onScrollEvent($event) {
  //   this.subjectCo.nativeElement.setAttribute('style', 'top:' + window['pageYOffset'] + 'px');
  //   this.jsPlumb.repaintEverything();
  // }

  @HostListener("window:resize", ["$event"])
  onSizeEvent($event) {
    this.jsPlumb.repaintEverything();
  }

  ngAfterViewInit() {
    this.jsPlumb = jsPlumb.getInstance({
      Container: this.elRef.nativeElement,
      ConnectionsDetachable: false,
      Endpoint: "Blank",
    });
  }

  toggleLearningObjectiveHandler(lo: LearningObjectiveVM) {
    if (lo.inList) {
      this.removeLearningObjective.emit(lo);
    } else {
      this.addLearningObjective.emit(lo);
    }
  }
  deselectSubject(subject: Subject) {
    if (this.selectedLearningObjective) {
      // deselect a selected learning objective is its collapsed and not in the list at the right
      subject.goals.forEach((g) => {
        if (
          this.selectedLearningObjective &&
          !this.selectedLearningObjective.inList &&
          g.learningObjectives.find(
            (l) => l.id === this.selectedLearningObjective.id
          )
        ) {
          this.selectedLearningObjective = null;
          this.learningObjectiveSelected.emit(null);
        }
      });
    }

    if (subject.goals.indexOf(this.selectedGoal) > -1) {
      this.selectedGoal = undefined;
    }

    // verwijder het huidige onderwerp uit de lijst van geselecteerde onderwerpen
    this.selectedSubjects.splice(this.selectedSubjects.indexOf(subject), 1);
    this.zone.onStable.pipe(take(1)).subscribe((_) => {
      this.drawConnections();
    });
  }

  selectSubject(subject) {
    let height;
    if (this.selectedGoal) {
      // Als er een koepeldoel geselecteerd is, onthoud de scroll positie
      height = this.elRef.nativeElement.clientHeight;
    }
    // Zet het huidige onderwerp bij de geselecteerde onderwerpen in dezelfde volgorde als de lijst
    // ,dus niet in de volgorde van selecteren.
    this.selectedSubjects.push(subject);
    this.selectedSubjects.sort(
      (a, b) =>
        this.domain.subjects.indexOf(a) - this.domain.subjects.indexOf(b)
    );
    const self = this;
    this.zone.onStable.pipe(take(1)).subscribe((_) => {
      self.drawConnections();

      if (this.selectedGoal) {
        if (
          this.domain.subjects.indexOf(subject) >
          this.domain.subjects.indexOf(this.getGoalSubject(this.selectedGoal))
        ) {
          window.scrollTo(
            0,
            this.scrollPositionSelected +
              (self.elRef.nativeElement.clientHeight - height)
          );
        }
      }
    });
  }

  private getGoalSubject(goal: Goal) {
    this.domain.subjects.forEach((subject) => {
      subject.goals.forEach((gl) => {
        if (gl.code === goal.code) {
          return subject;
        }
      });
    });
    return null;
  }
  deselectGoal() {
    this.selectedGoal = null;
    this.zone.onStable.pipe(take(1)).subscribe((_) => {
      this.drawConnections();
    });
  }

  idGoal(index, item: any) {
    return item ? item.id : index;
  }

  idLearningObjective(index, item: any) {
    return item ? item.id : index;
  }

  selectGoal(goal: Goal) {
    this.scrollPositionSelected = this.elRef.nativeElement.scrollTop;
    this.selectedGoal = goal;
    this.zone.onStable.pipe(take(1)).subscribe((_) => {
      this.drawConnections();
    });
  }
  public select = (learningObjective: LearningObjectiveVM) => {
    if (!learningObjective.inList) {
      learningObjective = { ...learningObjective, inList: false };
    }
    this.learningObjectiveSelected.emit(learningObjective);
  };
}
